/**
 * Real-time silver ALTIOR coin for the Club section.
 *
 * Both faces are generated at load from the Ø brand mark: it becomes a height
 * field that drives a normal map (raised, bevelled silver relief), a
 * metalness/roughness map (polished silver on deep-green enamel, like the
 * artwork) and a little cavity shading. The edge is reeded. A small studio environment is rendered once
 * so the metal has something real to reflect.
 *
 * three.js loads only when the section is near the viewport, and rendering
 * pauses off screen. If WebGL or the download fails, the CSS disc stays.
 */

const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';
let SIZE = 1024;

/** @param {string} src */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** @param {number} w @param {number} h */
function canvas2d(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d context');
  return { c, ctx };
}

/**
 * Separable box blur on a float field.
 *
 * @param {Float32Array} src
 * @param {number} n
 * @param {number} r
 */
function blur(src, n, r) {
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  for (let y = 0; y < n; y++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += src[y * n + Math.min(n - 1, Math.max(0, k))];
    for (let x = 0; x < n; x++) {
      tmp[y * n + x] = acc / (2 * r + 1);
      const add = Math.min(n - 1, x + r + 1);
      const sub = Math.max(0, x - r);
      acc += src[y * n + add] - src[y * n + sub];
    }
  }
  for (let x = 0; x < n; x++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += tmp[Math.min(n - 1, Math.max(0, k)) * n + x];
    for (let y = 0; y < n; y++) {
      out[y * n + x] = acc / (2 * r + 1);
      const add = Math.min(n - 1, y + r + 1);
      const sub = Math.max(0, y - r);
      acc += tmp[add * n + x] - tmp[sub * n + x];
    }
  }
  return out;
}

/**
 * Turns an artwork image into a white-on-black relief mask.
 * Opaque artwork on a solid colour uses brightness; transparent artwork uses alpha.
 *
 * @param {HTMLImageElement} img
 * @param {number} scale  fraction of the face the artwork spans
 * @param {number} [yOffset]
 */
function artworkMask(img, scale, yOffset = 0) {
  const { c, ctx } = canvas2d(SIZE, SIZE);
  const w = SIZE * scale;
  const h = (w * img.naturalHeight) / img.naturalWidth;
  const x0 = (SIZE - w) / 2;
  const y0 = (SIZE - h) / 2 + yOffset * SIZE;
  ctx.drawImage(img, x0, y0, w, h);
  const { data } = ctx.getImageData(0, 0, SIZE, SIZE);

  // Does the artwork itself have transparency? Only look inside where it was drawn.
  let transparent = 0;
  let sampled = 0;
  for (let y = Math.ceil(y0) + 2; y < Math.floor(y0 + h) - 2; y += 13) {
    for (let x = Math.ceil(x0) + 2; x < Math.floor(x0 + w) - 2; x += 13) {
      sampled++;
      if (data[(y * SIZE + x) * 4 + 3] < 200) transparent++;
    }
  }
  const useAlpha = transparent > sampled * 0.05;

  const mask = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < mask.length; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    const a = data[i * 4 + 3] / 255;
    if (useAlpha) {
      mask[i] = a;
    } else {
      const l = 0.299 * r + 0.587 * g + 0.114 * b;
      mask[i] = a * Math.min(1, Math.max(0, (l - 90) / 110));
    }
  }
  void c;
  return mask;
}

/**
 * Builds a coin face: rim, ring text, inner bead line and the artwork.
 *
 * @param {Float32Array} art
 * @param {string} ring
 * @param {string} family
 * @param {string} [caption]
 */
function faceHeight(art) {
  const { ctx } = canvas2d(SIZE, SIZE);
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = '#fff';
  const R = SIZE / 2;
  // Slim raised silver rim.
  ctx.beginPath();
  ctx.arc(R, R, R * 0.998, 0, Math.PI * 2);
  ctx.arc(R, R, R * 0.935, 0, Math.PI * 2, true);
  ctx.fill();
  const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
  const h = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < h.length; i++) h[i] = Math.max(data[i * 4] / 255, art[i]);
  return h;
}

/**
 * Normal, roughness and colour textures from a height field.
 *
 * @param {any} THREE
 * @param {Float32Array} raw
 */
function faceTextures(THREE, raw) {
  const n = SIZE;
  const k = n / 1024;
  const bevel = Math.max(2, Math.round(3 * k));
  // Three passes of box blur approximate a gaussian: a smooth rounded bevel
  // with no stair-stepping on the curves of the Ø.
  const h = blur(blur(blur(raw, n, bevel), n, bevel), n, bevel);
  const cavity = blur(raw, n, Math.round(12 * k)); // shade the enamel next to relief
  const soft = blur(raw, n, Math.max(1, Math.round(1.5 * k))); // anti-aliased material edges

  const normal = canvas2d(n, n);
  const rough = canvas2d(n, n);
  const color = canvas2d(n, n);
  const N = normal.ctx.createImageData(n, n);
  const Rg = rough.ctx.createImageData(n, n);
  const C = color.ctx.createImageData(n, n);

  const strength = 4.2 * k;
  let seed = 9;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };

  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = y * n + x;
      const l = h[y * n + Math.max(0, x - 1)];
      const r = h[y * n + Math.min(n - 1, x + 1)];
      const u = h[Math.max(0, y - 1) * n + x];
      const d = h[Math.min(n - 1, y + 1) * n + x];
      let nx = (l - r) * strength;
      let ny = (d - u) * strength;
      let nz = 1 / 16;
      const len = Math.hypot(nx, ny, nz);
      nx /= len;
      ny /= len;
      nz /= len;
      N.data[i * 4] = (nx * 0.5 + 0.5) * 255;
      N.data[i * 4 + 1] = (ny * 0.5 + 0.5) * 255;
      N.data[i * 4 + 2] = (nz * 0.5 + 0.5) * 255;
      N.data[i * 4 + 3] = 255;

      const raised = soft[i];
      const grain = (rnd() - 0.5) * 0.04;
      // Concentric brushing on the silver, like a turned finish.
      const dx = x - n / 2;
      const dy = y - n / 2;
      const brush = Math.sin(Math.hypot(dx, dy) * 0.9 + rnd() * 1.2) * 0.035;
      // Satin enamel field; polished, lightly brushed silver relief.
      const roughness = (0.62 + grain) * (1 - raised) + (0.2 + brush + grain * 0.5) * raised;
      Rg.data[i * 4] = 0;
      Rg.data[i * 4 + 1] = Math.max(0, Math.min(255, roughness * 255));
      Rg.data[i * 4 + 2] = raised * 255; // metalness
      Rg.data[i * 4 + 3] = 255;

      // Deep green enamel (#12291c, the brand green) meeting silver relief,
      // a touch darker where the enamel tucks against the raised edges.
      const occl = 1 - Math.max(0, cavity[i] - raised) * 0.5;
      const t = raised;
      const peel = 1 + (rnd() - 0.5) * 0.05 * (1 - t);
      C.data[i * 4] = (18 * (1 - t) + 226 * t) * occl * peel;
      C.data[i * 4 + 1] = (41 * (1 - t) + 228 * t) * occl * peel;
      C.data[i * 4 + 2] = (28 * (1 - t) + 233 * t) * occl * peel;
      C.data[i * 4 + 3] = 255;
    }
  }
  normal.ctx.putImageData(N, 0, 0);
  rough.ctx.putImageData(Rg, 0, 0);
  color.ctx.putImageData(C, 0, 0);

  const tex = (/** @type {HTMLCanvasElement} */ c, srgb = false) => {
    const t = new THREE.CanvasTexture(c);
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 16;
    t.generateMipmaps = true;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    return t;
  };
  const rm = tex(rough.c);
  return { normalMap: tex(normal.c), roughnessMap: rm, metalnessMap: rm, map: tex(color.c, true) };
}

/** Vertical reeding for the coin's edge. @param {any} THREE */
function reedTextures(THREE) {
  const w = 16;
  const normal = canvas2d(w, 4);
  const N = normal.ctx.createImageData(w, 4);
  for (let x = 0; x < w; x++) {
    const nx = Math.sin((x / w) * Math.PI * 2) * 0.75;
    const nz = Math.sqrt(1 - nx * nx);
    for (let y = 0; y < 4; y++) {
      const i = (y * w + x) * 4;
      N.data[i] = (nx * 0.5 + 0.5) * 255;
      N.data[i + 1] = 128;
      N.data[i + 2] = (nz * 0.5 + 0.5) * 255;
      N.data[i + 3] = 255;
    }
  }
  normal.ctx.putImageData(N, 0, 0);
  const t = new THREE.CanvasTexture(normal.c);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(180, 1);
  return t;
}

/** A small photo studio for the silver to reflect. @param {any} THREE @param {any} renderer */
function studio(THREE, renderer) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x6f675e);
  const panel = (/** @type {number[]} */ pos, /** @type {number[]} */ size, /** @type {number[]} */ rgb) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(size[0], size[1]),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(rgb[0], rgb[1], rgb[2]), side: THREE.DoubleSide })
    );
    m.position.set(pos[0], pos[1], pos[2]);
    m.lookAt(0, 0, 0);
    scene.add(m);
  };
  panel([5, 5, 5], [5, 3], [5, 4.8, 4.4]); // warm key softbox, top right
  panel([-6, 0, 3], [2, 6], [1.1, 1.2, 1.3]); // cool fill, left
  panel([0, 7, 0], [8, 2], [1.2, 1.15, 1.1]); // overhead
  panel([0, -6, 2], [10, 4], [0.35, 0.28, 0.2]); // warm floor bounce
  panel([-2, 0, -7], [6, 6], [0.5, 0.45, 0.4]); // back wall
  const pmrem = new THREE.PMREMGenerator(renderer);
  const env = pmrem.fromScene(scene, 0.02).texture;
  pmrem.dispose();
  return env;
}

/** @param {HTMLElement} host */
async function start(host) {
  const frontUrl = host.dataset.front;
  if (!frontUrl) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // The coin's box tops out at 520px (300px on mobile) and the texture is
  // mipmapped for display anyway, so the full 2048/1536 this used to
  // generate at was real CPU/GPU cost spent well past what ever reaches the
  // screen. 1280/1024 still oversamples the largest on-screen size by a
  // comfortable margin (~1.3x the 2x-DPR footprint) with no visible
  // softening, for under half the texture-generation work and GPU memory.
  SIZE = window.matchMedia('(max-width: 749px)').matches ? 1024 : 1280;

  const [THREE, frontImg] = await Promise.all([
    import(/* @vite-ignore */ THREE_URL),
    loadImage(frontUrl),
  ]);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  scene.environment = studio(THREE, renderer);

  const key = new THREE.DirectionalLight(0xffe2b8, 1.4);
  // Light comes from the top right of the section.
  key.position.set(4, 4.6, 7.5);
  scene.add(key);

  // Both faces carry the Ø mark, sized as in the brand artwork.
  const face = faceTextures(THREE, faceHeight(artworkMask(frontImg, 1.0)));
  const front = face;
  const back = face;

  const metal = (/** @type {any} */ t) =>
    new THREE.MeshStandardMaterial({
      metalness: 1,
      roughness: 1,
      map: t.map,
      normalMap: t.normalMap,
      normalScale: new THREE.Vector2(1, 1),
      roughnessMap: t.roughnessMap,
      metalnessMap: t.metalnessMap,
      envMapIntensity: 1.25,
    });

  const R = 1;
  const T = 0.13;
  const coin = new THREE.Group();

  // 96/150 segments (was 160/240) -- at a 520px max diameter that's still
  // under 4deg per facet, imperceptible, for noticeably fewer vertices.
  const faceGeo = new THREE.CircleGeometry(R, 96);
  const frontMesh = new THREE.Mesh(faceGeo, metal(front));
  frontMesh.position.z = T / 2;
  const backMesh = new THREE.Mesh(faceGeo, metal(back));
  backMesh.rotation.y = Math.PI;
  backMesh.position.z = -T / 2;

  const edgeGeo = new THREE.CylinderGeometry(R, R, T, 150, 1, true);
  edgeGeo.rotateX(Math.PI / 2);
  const edge = new THREE.Mesh(
    edgeGeo,
    new THREE.MeshStandardMaterial({
      color: 0xd9dbe0,
      metalness: 1,
      roughness: 0.28,
      normalMap: reedTextures(THREE),
      envMapIntensity: 1.1,
    })
  );

  coin.add(frontMesh, backMesh, edge);
  // Spin first, then tilt: the tilt changes the axis the coin turns about.
  coin.rotation.order = 'ZXY';
  scene.add(coin);

  // Soft shadow on the wall, down and to the left (light is top right). A
  // blurred CSS disc behind the canvas, narrowed as the coin turns edge-on.
  const shadowEl = document.createElement('span');
  shadowEl.className = 'standards-ledger__coin-shadow';
  host.prepend(shadowEl);
  const lightDir = new THREE.Vector3(4, 4.6, 7.5).normalize();
  
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 50);

  const canvas = renderer.domElement;
  canvas.className = 'standards-ledger__mark-canvas';
  host.appendChild(canvas);

  const resize = () => {
    const w = host.clientWidth * 2;
    const h = host.clientHeight * 2;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    camera.aspect = w / h;
    // The coin spans ~74% of its box; the canvas is 1.3x the box.
    const fov = (camera.fov * Math.PI) / 180;
    const visible = (2 * R) / (0.74 / 2);
    camera.position.set(0, 0, visible / (2 * Math.tan(fov / 2) * Math.min(1, camera.aspect)));
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  };
  resize();
  new ResizeObserver(resize).observe(host);

  // Planar shadow: the coin's real outline projected onto the wall along the
  // light direction (top right, in front). Every frame the disc's centre and
  // two in-plane radii are projected, then drawn as a blurred CSS ellipse.
  const WALL_Z = -1.3;
  const P = new THREE.Vector3();
  const axisU = new THREE.Vector3();
  const axisV = new THREE.Vector3();
  /** @param {any} v */
  const toWall = (v) => v.addScaledVector(lightDir, -(v.z - WALL_Z) / lightDir.z);
  /** @param {any} v */
  const toScreen = (v) => {
    v.project(camera);
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const ox = (host.clientWidth - cw) / 2;
    const oy = (host.clientHeight - ch) / 2;
    return [ox + ((v.x + 1) / 2) * cw, oy + ((1 - v.y) / 2) * ch];
  };
  const updateShadow = () => {
    coin.updateMatrixWorld();
    axisU.set(R, 0, 0).applyQuaternion(coin.quaternion);
    axisV.set(0, R, 0).applyQuaternion(coin.quaternion);
    const c = toScreen(toWall(P.copy(coin.position)));
    const u = toScreen(toWall(P.copy(coin.position).add(axisU)));
    const v = toScreen(toWall(P.copy(coin.position).add(axisV)));
    // Element is a 100px circle; map its radius (50px) onto the projected radii.
    const a = (u[0] - c[0]) / 50;
    const b = (u[1] - c[1]) / 50;
    const cc = (v[0] - c[0]) / 50;
    const d = (v[1] - c[1]) / 50;
    shadowEl.style.transform = `matrix(${a}, ${b}, ${cc}, ${d}, ${c[0] - 50}, ${c[1] - 50})`;
  };

  host.classList.add('is-3d');

  if (reduced) {
    coin.rotation.set(-0.08, -0.45, 0);
    updateShadow();
    renderer.render(scene, camera);
    return;
  }

  let visible = true;
  let last = performance.now();
  let raf = 0;

  const frame = (/** @type {number} */ now) => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const t = now / 1000;
    coin.rotation.y += dt * 0.8; // anticlockwise (seen from above), constant speed
    coin.rotation.x = -0.2;
    coin.rotation.z = 0.14;
    coin.position.y = Math.sin(t / 1.3) * 0.03;
    updateShadow();
    renderer.render(scene, camera);
    raf = visible && !document.hidden ? requestAnimationFrame(frame) : 0;
  };

  const resume = () => {
    if (!raf && visible && !document.hidden) {
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
  };

  new IntersectionObserver(
    (entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      resume();
    },
    { rootMargin: '80px' }
  ).observe(host);
  document.addEventListener('visibilitychange', resume);
  resume();
}

function init() {
  document.querySelectorAll('[data-coin-3d]').forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.coinStarted) return;
    el.dataset.coinStarted = 'true';
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          io.disconnect();
          start(el).catch(() => {
            /* WebGL or the download failed: the CSS disc stays in place. */
          });
        }
      },
      { rootMargin: '600px' }
    );
    io.observe(el);
  });
}

init();
document.addEventListener('shopify:section:load', init);
